import { useState } from "react";
import AdminLayout from "@/layouts/AdminLayout";
import api from "@/services/api";
import { useApi } from "@/hooks/useApi";
import { useDebounce } from "@/hooks/useDebounce";
import { useNotifications } from "@/context/NotificationContext";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import ClientModal from "./components/ClientModal";
import ClientDetailsModal from "./components/ClientDetailsModal";

interface Client {
  id: string;
  company_name: string;
  email: string;
  full_name: string;
  plan: "starter" | "growth" | "enterprise" | string;
  status: "active" | "paused" | "inactive" | string;
  projects: number;
  mrr: number;
  avatar_url: string | null;
  industry?: string | null;
}

export default function AdminClients() {
  const { showToast } = useNotifications();
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editClient, setEditClient] = useState<any>(null);
  const [detailsClient, setDetailsClient] = useState<any>(null);


  const debouncedSearch = useDebounce(search, 300);

  const { data: clients, loading, refetch } = useApi<Client[]>(`/clients?search=${encodeURIComponent(debouncedSearch)}`);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/clients/${deleteId}`);
      showToast("success", "Client deleted.");
      refetch();
    } catch {
      showToast("error", "Failed to delete client.");
    }
    setDeleteId(null);
  };

  const handleCreateClient = async (payload: any) => {
    try {
      await api.post("/clients", payload);
      showToast("success", "Client created.");
      refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || "Failed to create client";
      showToast("error", msg);
      throw e;
    }
  };

  const handleUpdateClient = async (payload: any) => {
    if (!editClient) return;
    try {
      await api.patch(`/clients/${editClient.id}`, payload);
      showToast("success", "Client updated.");
      refetch();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || "Failed to update client";
      showToast("error", msg);
      throw e;
    }
  };

  const planColors: Record<string, string> = {
    starter: "text-silver border-dark-border bg-dark-border/30",
    growth: "text-electric-blue border-electric-blue/30 bg-electric-blue/10",
    enterprise: "text-cyber-green border-cyber-green/30 bg-cyber-green/10",
  };

  const statusColors: Record<string, string> = {
    active: "text-cyber-green",
    paused: "text-cyber-orange",
    inactive: "text-silver-dim",
  };

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Clients
            </h1>
            <p className="text-silver text-sm mt-1">{clients?.length || 0} total clients</p>
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 bg-electric-blue text-deep-black font-semibold px-4 py-2.5 rounded-xl text-sm hover:shadow-glow-blue transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" /> Add Client
          </button>
        </div>

        <div className="relative max-w-sm">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-search-line text-silver-dim text-sm" />
          </div>
          <input
            type="text"
            placeholder="Search clients..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-dark-card border border-dark-border rounded-xl pl-9 pr-4 py-2.5 text-white text-sm placeholder-silver-dim focus:outline-none focus:border-electric-blue/50 transition-colors"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-electric-blue border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-dark-border">
                    <th className="text-left text-silver-dim font-medium py-3 px-5">Client</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4 hidden md:table-cell">Plan</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4 hidden lg:table-cell">Projects</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4 hidden lg:table-cell">MRR</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4 hidden sm:table-cell">Status</th>
                    <th className="text-left text-silver-dim font-medium py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients?.map((client) => (
                    <tr key={client.id} className="border-b border-dark-border/50 hover:bg-dark-panel/50 transition-colors">
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-electric-blue/10 border border-electric-blue/20 text-electric-blue text-xs font-bold flex-shrink-0">
                            {client.company_name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                          </div>
                          <div>
                            <p className="text-white font-semibold">{client.company_name}</p>
                            <p className="text-silver-dim text-xs">{client.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 hidden md:table-cell">
                        <span
                          className={`text-xs px-2.5 py-1 rounded-full border capitalize ${planColors[String(client.plan)] || "text-silver border-dark-border"}`}
                        >
                          {client.plan}
                        </span>
                      </td>
                      <td className="py-4 px-4 hidden lg:table-cell">
                        <span className="text-silver text-xs">{client.projects}</span>
                      </td>
                      <td className="py-4 px-4 hidden lg:table-cell">
                        <span className="text-white text-xs font-semibold">${client.mrr}/mo</span>
                      </td>
                      <td className="py-4 px-4 hidden sm:table-cell">
                        <span
                          className={`text-xs font-medium capitalize ${statusColors[String(client.status)] || "text-silver-dim"}`}
                        >
                          {client.status}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setDetailsClient(client)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-electric-blue hover:border-electric-blue/40 transition-all cursor-pointer"
                            aria-label={`View ${client.company_name}`}
                          >
                            <i className="ri-eye-line text-xs" />
                          </button>
                          <button
                            onClick={() => setEditClient(client)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-white hover:border-electric-blue/40 transition-all cursor-pointer"
                            aria-label={`Edit ${client.company_name}`}
                          >
                            <i className="ri-edit-line text-xs" />
                          </button>
                          <button
                            onClick={() => setDeleteId(client.id)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg border border-dark-border text-silver-dim hover:text-cyber-red hover:border-cyber-red/30 transition-all cursor-pointer"
                            aria-label={`Delete ${client.company_name}`}
                          >
                            <i className="ri-delete-bin-line text-xs" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete Client"
        message="Are you sure you want to delete this client? This action cannot be undone."
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />

      <ClientModal
        open={createOpen}
        mode="create"
        onClose={() => setCreateOpen(false)}
        onSubmit={async (payload) => {
          await handleCreateClient(payload);
        }}
      />

      <ClientModal
        open={!!editClient}
        mode="edit"
        initial={editClient}
        onClose={() => setEditClient(null)}
        onSubmit={async (payload) => {
          await handleUpdateClient(payload);
        }}
      />

      <ClientDetailsModal
        open={!!detailsClient}
        client={detailsClient}
        onClose={() => setDetailsClient(null)}
      />
    </AdminLayout>
  );
}


